import React, { useState, useEffect } from "react"
import Modal from "../../../../global/components/modal"
import useRetrieveData from "../../../../global/hooks/useRetrieveData"
import { get, size } from "../../../../global/lib/lodash"
import { Text } from "../../../../global/components/text"
import { Row } from "../../../../global/components/row"
import { List } from "@mui/material"
import AddIcon from "@mui/icons-material/Add"
import Confirm from "../../../../global/components/confirm"
import { CenterLoader } from "../../../../global/styles"
import sendData from "../../../../redux/actions/sendData"
import NoData from "../../../../global/components/NoData"
import Switch from "../../../../global/components/switch"
import ReportListItem from "../../ReportList/ReportListItem"

const useTargetScoresFromScorecard = props => {
    const scnum = get(props, 'scorecardId', null);
    const aid = get(props, 'assessment.data.id');
    const page = {
        size: 100,
        number: 1,
    }

    const url = props.template ? `/assessment-templates` : '/assessments';

    const targetscores = useRetrieveData({ url }, {
        waitFor: scnum,
        build: ['assessments'],
        useEffects: true,
        params: {
            include: 'realm,realm.parent,authors',
            filter: {
                forecast_clone_scorecard_context: scnum,
                realm: props.useLocation ? `${get(props, 'assessment.data.realmId')}` : undefined,
            },
            page
        }
    }, [props.useLocation]);

    return {
        ...targetscores,
        // PFM-2566: Skip rows whose forecastScores is null/empty — ScoreDisplay keys off
        // Object.keys(forecastScores) and would WSOD on those rows, and there's nothing
        // useful to copy from them anyway.
        data: targetscores.data.filter(f => f.id != aid && f.forecastScores && size(Object.keys(f.forecastScores))),
    }
}

const ScoreCopyRow = ({ item, onSelect }) => (
    <ReportListItem
        item={{ ...item, scores: item.forecastScores }}
        onSelect={onSelect}
        ListIcon={AddIcon}
        alwaysShowScore
        alwaysShowIcon
        scoreLabel="TARGET:"
    />
)

const ScoreCopy = props => {
    const [state, setState] = useState({
        useLocation: true,
    })
    
    const scores = useTargetScoresFromScorecard({ ...props, ...state });
    const tempscores = useTargetScoresFromScorecard({ ...props, template: true, ...state });
    
    const [confirm, setConfirm] = useState({});

    const selectRow = r => {
        const aid = get(props, 'assessment.data.id');

        setConfirm({
            title: 'Are you sure?',
            content: <div>You are creating Target scores for this Assessment from <b>{r.name}</b>. Would you like to continue?</div>,
            onOk: async () => {
                try {
                    await sendData({
                        url: `/assessments/${aid}/target-scores`,
                        method: 'PATCH',
                        data: {
                            data: {
                                type: "assessments",
                                id: aid,
                                attributes: {
                                    target_origin_id: r.id,
                                }
                            }
                        }
                    })
    
                    setConfirm({});
                    props.updateScoresAfterCopy();
                    props.onClose({ assigned: true });
                } catch (err) {
                    alert(err.toString());
                }
            },
            onCancel: () => {
                setConfirm({});
            }
        })
    }

    const isShowingNone = !scores.loading && !tempscores.loading && !size(scores.data) && !size(tempscores.data);

    useEffect(() => {
        props.isShowingNone && props.isShowingNone(isShowingNone);
    }, [isShowingNone]);

    return (
        <div>
            <Text>Add target scores to this assessment from the targets made in previous assessments.</Text>
            
            <div>
                <Row>
                    <Text>Use Location</Text>
                    <Switch checked={state.useLocation} onChange={() => setState(s => ({ ...s, useLocation: !s.useLocation }))} />
                </Row>
            </div>
            
            <div style={{ width: '100%', height: 10 }} />

            {!isShowingNone && <div>
                <Text type="title_medium" bold>Use a Template</Text>
                <List disablePadding>
                    {tempscores.data.map((r) => <ScoreCopyRow key={r.id} item={r} onSelect={selectRow} />)}
                    {tempscores.loading && <CenterLoader />}
                </List>

                <div style={{ width: '100%', height: 50 }} />

                <Text type="title_medium" bold>Use a Previous Assessment</Text>
                <List disablePadding>
                    {scores.data.map((r) => <ScoreCopyRow key={r.id} item={r} onSelect={selectRow} />)}
                    {scores.loading && <CenterLoader />}
                </List>
            </div>}

            {isShowingNone && 
                <NoData 
                    secondaryText="Target scores are copied from previously created targets; “Add New Target Scores” on a previous assessment or template to copy them here." 
                    noDataText="No Target Scores to Copy"
                    showButtons={state.useLocation}
                    buttons={[{ text: 'show more', onClick: () => setState(s => ({ ...s, useLocation: false })) }]}
            />}
            <Confirm {...confirm} />
        </div>
    )
}


export const ScoreCopyModal = props => {
    const open = !!props.open;
    
    return (
        <Modal {...props} open={!!open}>
            {open && <ScoreCopy {...props} />}
        </Modal>
    )
}
